<template>
  <div>
    <v-container class="page-title page-title--advanced">
      <v-row>
        <v-col class="page-title__title-box">
          <h4 class="page-title__title">{{ $t('heading.instance.advanced.autoUpdates.title') }}</h4>
        </v-col>
      </v-row>
    </v-container>
    <v-container>
      <v-row>
        <v-col
          v-if="!fetchingAutoUpdates && updaterBlockedBy.length"
          cols="12"
          class="pb-2"
        >
          <v-alert type="warning" dense outlined class="mb-0">
            {{
              $t('heading.instance.advanced.autoUpdates.blockedBy', {
                constants: updaterBlockedBy.join(', '),
              })
            }}
          </v-alert>
        </v-col>

        <v-col
          v-if="$attrs.instance.auto_updates.wordpress"
          cols="12" class="pb-2"
        >
          <auto-updates-card-skeleton v-if="fetchingAutoUpdates" />
          <v-card elevation="4" v-else>
            <v-card-text
              class="d-flex justify-space-between pb-6 text--gray-darken2"
              :class="autoUpdatesEnabled ? '' : ''"
            >
              <div>
                <h6>{{ $t('heading.instance.advanced.autoUpdates.upgrade.title') }}</h6>
                <p class="mb-0  mt-1">
                  {{ $t('heading.instance.advanced.autoUpdates.upgrade.subtitle') }}
                </p>
              </div>
              <div class="d-flex align-center">
                <ButtonSwitch
                  :loading="submitAutoUpgrades"
                  :value="autoUpdates.auto_update ? true : false"
                  @click.native.stop.prevent="toggleAutoUpgrades"
                />
              </div>
            </v-card-text>
            <v-expand-transition>
              <div v-if="autoUpdates.auto_update">
                <v-divider />
                <v-card-text>
                  <v-select
                    outlined
                    hide-details=""
                    xlarge
                    :value="autoUpdates.auto_update"
                    @change="changeAutoUpdate"
                    :items="options"
                    item-text="label"
                    item-value="value"
                    :menu-props="{
                      offsetY: true,
                    }"
                  >
                    <template v-slot:no-data>
                      <loader />
                    </template>
                  </v-select>
                </v-card-text>
              </div>
            </v-expand-transition>
          </v-card>
        </v-col>

        <v-col cols="12" class="pb-2">
          <auto-updates-card-skeleton v-if="fetchingAutoUpdates" />

          <v-card elevation="4" v-else>
            <v-card-text class="d-flex justify-space-between text--gray-darken2">
              <div>
                <h6>{{ $t('heading.instance.advanced.autoUpdates.upgrade.plugin.title') }}</h6>
                <p class="mb-0  mt-1">
                  {{ $t('heading.instance.advanced.autoUpdates.upgrade.plugin.subtitle') }}
                </p>
                <p
                  v-if="autoUpdates.plugins === 'partial'"
                  class="mb-0 mt-2 warning--text"
                >
                  {{ $t('heading.instance.advanced.autoUpdates.upgrade.plugin.partial') }}
                </p>
              </div>
              <div class="d-flex align-center">
                <ButtonSwitch
                  :loading="submitAutoUpgradePlugins"
                  :value="pluginsAutoUpdateOn"
                  @click.native.stop.prevent="toggleAutoUpgradePlugins"
                />
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" class="pb-2">
          <auto-updates-card-skeleton v-if="fetchingAutoUpdates" />

          <v-card elevation="4" v-else>
            <v-card-text class="d-flex justify-space-between text--gray-darken2">
              <div>
                <h6>{{ $t('heading.instance.advanced.autoUpdates.upgrade.theme.title') }}</h6>
                <p class="mb-0 mt-1">
                  {{ $t('heading.instance.advanced.autoUpdates.upgrade.theme.subtitle') }}
                </p>
                <p
                  v-if="autoUpdates.themes === 'partial'"
                  class="mb-0 mt-2 warning--text"
                >
                  {{ $t('heading.instance.advanced.autoUpdates.upgrade.theme.partial') }}
                </p>
              </div>
              <div class="d-flex align-center">
                <ButtonSwitch
                  :loading="submitAutoUpgradeThemes"
                  :value="themesAutoUpdateOn"
                  @click.native.stop.prevent="toggleAutoUpgradeThemes"
                />
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import Api from "@/apis/Api";

import ButtonSwitch from "../../../components/buttons/ButtonSwitch.vue";
import Loader from "../../../components/Loader.vue";
import AutoUpdatesCardSkeleton from "../../../components/cards/AutoUpdatesCardSkeleton.vue";

export default {
  mounted() {
    this.fetching = false;
    this.fetchAutoUpdates();
  },
  components: { 
    Loader, 
    AutoUpdatesCardSkeleton, 
    ButtonSwitch 
  },
  data: function () {
    return {
      submitAutoUpgradeThemes: false,
      submitAutoUpgradePlugins: false,
      submitAutoUpgrades: false,
      autoUpdatesEnabled: false,
      fetching: true,
      fetchingAutoUpdates: true,
      submitting: false,
      autoUpdates: {},
      options: [
        {
          label: this.$t('heading.instance.advanced.autoUpdates.upgrade.option.latest'),
          value: 1,
        },
        {
          label: this.$t('heading.instance.advanced.autoUpdates.upgrade.option.minor'),
          value: 2
        },
      ],
      applicationId: this.$route.params.id,
    };
  },
  computed: {
    updaterBlockedBy() {
      const blocked = this.autoUpdates.updater_blocked_by;
      return Array.isArray(blocked) ? blocked : [];
    },
    pluginsAutoUpdateOn() {
      return !!this.autoUpdates.auto_update_plugins && this.autoUpdates.plugins !== 'partial';
    },
    themesAutoUpdateOn() {
      return !!this.autoUpdates.auto_update_themes && this.autoUpdates.themes !== 'partial';
    },
  },
  methods: {
    async toggleAutoUpgradeThemes() {
      this.submitAutoUpgradeThemes = true

      const repair = this.autoUpdates.themes === 'partial';
      const newData = {
        auto_update: this.autoUpdates.auto_update,
        auto_update_plugins: this.autoUpdates.auto_update_plugins,
        auto_update_themes: repair || !this.autoUpdates.auto_update_themes ? 1 : 0,
      };
      if (repair) {
        newData.enable_all_themes = 1;
      }

      try {
        await this.saveChanges(newData)
      } catch(error) {
        console.log(error)
      } finally {
        this.submitAutoUpgradeThemes = false
      }
    },
    async toggleAutoUpgradePlugins() {
      this.submitAutoUpgradePlugins = true

      const repair = this.autoUpdates.plugins === 'partial';
      const newData = {
        auto_update: this.autoUpdates.auto_update,
        auto_update_plugins: repair || !this.autoUpdates.auto_update_plugins ? 1 : 0,
        auto_update_themes: this.autoUpdates.auto_update_themes,
      };
      if (repair) {
        newData.enable_all_plugins = 1;
      }
      try {
        await this.saveChanges(newData)
      } catch(error) {
        console.log(error)
      } finally {
        this.submitAutoUpgradePlugins = false
      }
    },
    async toggleAutoUpgrades() {
      this.submitAutoUpgrades = true

      const newData = {
        auto_update: !this.autoUpdates.auto_update ? 1 : 0,
        auto_update_plugins: this.autoUpdates.auto_update_plugins,
        auto_update_themes: this.autoUpdates.auto_update_themes,
      };
      try {
        await this.saveChanges(newData)
      } catch(error) {
        console.log(error)
      } finally {
        this.submitAutoUpgrades = false
      }
    },
    async changeAutoUpdate(value) {
        this.submitAutoUpgrades = true

        const newData = {
          auto_update: value,
          auto_update_plugins: this.autoUpdates.auto_update_plugins,
          auto_update_themes: this.autoUpdates.auto_update_themes,
        };

        try {
          await this.saveChanges(newData)
        } catch(error) {
          console.log(error)
        } finally {
          this.submitAutoUpgrades = false
        }
      },
    
    fetchAutoUpdates() {
      this.fetchingAutoUpdates = true;
      Api.get(`/instances/${this.applicationId}/wordpress/auto-update`)
        .then((response) => {
          this.autoUpdates = response.data.data;
        })
        .catch((error) => {
          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: Api.getErrorMessage(error),
          });
        })
        .finally(() => {
          this.fetchingAutoUpdates = false;
        });
    },
    async saveChanges(data = this.autoUpdates) {
      this.submitting = true;

      try {
        await Api.put(
          `/instances/${this.applicationId}/wordpress/auto-update`,
          data
        )
        this.$store.dispatch("addAlert", {
            success: true,
            successMessage: this.$t('notification.instance.settings.update')
          });
          await this.refreshAutoUpdates();
          return true
      } catch(error) {
        this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: Api.getErrorMessage(error),
          });
          return false
      } finally {
        this.submitting = false;
      }
    },
    refreshAutoUpdates() {
      return Api.get(`/instances/${this.applicationId}/wordpress/auto-update`)
        .then((response) => {
          this.autoUpdates = response.data.data;
        })
        .catch(() => {
          // Keep local state when refresh fails; the save itself already succeeded.
        });
    },
  },
};
</script>